import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import PublicLinkField from '@components/PublicLinkField';
import { Select } from '@components/Select';
import Button from '@components/Button';
import { SectionTitle, Hint } from '@features/ChannelsForms/FormSection';
import { useTranslation, useThrottle, useCopyToClipboard } from '@hooks';
import { getChatWidgetScript, normalizeWidgetAlignment } from '@constants/widget';
import { widgetAlignmentOptions, widgetOffsetFieldProps } from '@helpers/widget-position';
import { getChatLink } from '@constants/liveChat';
import AllowedSites from './AllowedSites';
import SigningSecret from './SigningSecret';

const Container = styled('div')({
    display: 'grid',
    gridGap: '0 24px',
    gridTemplateAreas:
        '"title title" "select1 select1" "offsetX offsetY" "input2 input2" "sites sites" "secret secret" "link link"',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 600px)': {
        gridTemplateAreas: '"title" "select1" "offsetX" "offsetY" "input2" "sites" "secret" "link"',
        gridTemplateColumns: '100%',
    },
});

const LiveChatCode = ({ liveChatPublicId, liveChatId }) => {
    const { control } = useFormContext();
    const { t } = useTranslation();

    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    const script = getChatWidgetScript(liveChatPublicId);
    const chatLink = getChatLink(liveChatPublicId);

    const renderOffsetField = (name, label, gridArea) => (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error } }) => (
                <Input label={label} {...widgetOffsetFieldProps(field, error, t)} fullWidth sx={{ gridArea }} />
            )}
        />
    );

    return (
        <Container>
            <SectionTitle sx={{ gridArea: 'title', marginBottom: '8px' }}>
                {t('SettingsChannels.websiteWidget')}
            </SectionTitle>
            <Controller
                control={control}
                name="alignment"
                render={({ field, ref }) => (
                    <Select
                        {...field}
                        value={normalizeWidgetAlignment(field.value)}
                        ref={ref}
                        label={t('SettingsChannels.widgetPosition')}
                        options={widgetAlignmentOptions(t)}
                        required
                        style={{ gridArea: 'select1' }}
                    />
                )}
            />

            {renderOffsetField('offsetX', t('SettingsChannels.widgetOffsetX'), 'offsetX')}
            {renderOffsetField('offsetY', t('SettingsChannels.widgetOffsetY'), 'offsetY')}

            <div style={{ gridArea: 'input2' }}>
                <Hint sx={{ marginBottom: '8px' }}>{t('SettingsChannels.addCodeBelow')}</Hint>
                <Input
                    inputProps={{ value: script }}
                    label={t('ShowcaseWidgets.widgetCode')}
                    fullWidth
                    multiline
                    minRows={3}
                />
                <Button variant="outlined" onClick={() => onCopyClick(script)}>
                    {t('buttons.copyCode')}
                </Button>
            </div>

            <div style={{ gridArea: 'sites', marginTop: 24 }}>
                <AllowedSites />
            </div>

            <div style={{ gridArea: 'secret', marginTop: 24 }}>
                <SigningSecret liveChatId={liveChatId} />
            </div>

            <div style={{ gridArea: 'link', marginTop: 24 }}>
                <SectionTitle sx={{ marginBottom: '8px' }}>{t('SettingsChannels.chatLink')}</SectionTitle>
                <Hint sx={{ marginBottom: '8px' }}>{t('SettingsChannels.chatLinkHint')}</Hint>
                <PublicLinkField url={chatLink} />
                <Hint>{t('SettingsChannels.chatLinkParams')}</Hint>
            </div>
        </Container>
    );
};

export default LiveChatCode;
